import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { escapeHtml, escapeAttr } from './html.js';
export { escapeHtml, escapeAttr } from './html.js';
export function classifyLink(url) {
    const lowerUrl = url.toLowerCase();
    const bookingKeywords = [
        'book', 'reserve', 'reservation', 'checkout', 'cart',
        'availability', 'rates', 'pricing', 'package',
    ];
    if (bookingKeywords.some((keyword) => lowerUrl.includes(keyword)))
        return 'booking';
    const infoKeywords = ['about', 'contact', 'faq', 'help', 'info', 'details'];
    if (infoKeywords.some((keyword) => lowerUrl.includes(keyword)))
        return 'informational';
    return 'external';
}
export function getLinkClasses(linkType) {
    const base = 'inline-flex items-center gap-1 font-medium underline decoration-2 underline-offset-2 transition-all hover:gap-2';
    if (linkType === 'booking') {
        return `${base} text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 decoration-blue-600 dark:decoration-blue-400`;
    }
    if (linkType === 'informational') {
        return `${base} text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 decoration-green-600 dark:decoration-green-400`;
    }
    return `${base} text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 decoration-purple-600 dark:decoration-purple-400`;
}
export function shortenLinkText(text, url) {
    const isUrlText = text === url || text.startsWith('http://') || text.startsWith('https://');
    if (!isUrlText)
        return text;
    try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname.replace('www.', '');
        const path = urlObj.pathname;
        if (path && path !== '/') {
            const parts = path.split('/').filter((part) => part);
            if (parts.length > 0) {
                const last = parts[parts.length - 1];
                const display = last.length > 20 ? `${last.substring(0, 20)}...` : last;
                return `${domain}/.../${display}`;
            }
        }
        return domain;
    }
    catch {
        return text.length > 50 ? `${text.substring(0, 47)}...` : text;
    }
}
const chatRenderer = new marked.Renderer();
chatRenderer.link = ({ href, title, tokens }) => {
    const text = tokens
        .map((token) => token.raw || ('text' in token ? token.text : '') || '')
        .join('');
    const linkType = classifyLink(href);
    const classes = getLinkClasses(linkType);
    const icon = linkType === 'booking' ? '🎫' : '🔗';
    const displayText = shortenLinkText(text, href);
    const safeHref = escapeAttr(href);
    const safeTitle = escapeAttr(title || href);
    const safeDisplayText = escapeHtml(displayText);
    return `<a href="${safeHref}" class="${classes}" data-link-type="${linkType}" data-url="${safeHref}" title="${safeTitle}" target="_blank" rel="noopener noreferrer"><span class="link-icon">${icon}</span> <span class="link-text">${safeDisplayText}</span><svg class="link-external-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg></a>`;
};
marked.setOptions({ breaks: true, gfm: true });
const PURIFY_CONFIG = {
    // 'target' is not in DOMPurify's default attribute allowlist — without it
    // chat links navigate the host page the widget is embedded in (killing the
    // conversation) instead of opening a new tab. rel="noopener noreferrer" is
    // always emitted alongside it by chatRenderer.link.
    ADD_ATTR: ['data-link-type', 'data-url', 'target'],
};
export function renderChatMarkdown(content) {
    const safe = content ?? '';
    try {
        const html = marked(safe, { renderer: chatRenderer });
        return DOMPurify.sanitize(html, PURIFY_CONFIG);
    }
    catch {
        const fallbackHtml = escapeHtml(safe).replace(/\n/g, '<br>');
        return DOMPurify.sanitize(fallbackHtml, PURIFY_CONFIG);
    }
}
